import type { Table } from 'dexie'
import {
  db,
  ALL_TABLES,
  type ObraConfig,
  type TopicoProgresso,
  type LancamentoFinanceiro,
  type Documento,
  type Alerta,
  type DiarioEntry,
  type OrcamentoMaterial,
  type ObraPendencia,
  type ObraPendenciaStatus,
} from './db.js'
import { clearAllDocumentFiles, deleteDocumentBlob, writeDocumentBlob } from './documentStorage.js'
import { clearAppLocalState, clearServiceWorkerCaches } from './appStorage.js'

const now = () => new Date().toISOString()

// ── ObraConfig (singleton) ───────────────────────────────────

export async function getObraConfig(): Promise<ObraConfig | undefined> {
  return db.obra_config.get(1)
}

export async function saveObraConfig(
  patch: Partial<Omit<ObraConfig, 'id' | 'criadoEm' | 'atualizadoEm'>> & { criadoEm?: string },
): Promise<void> {
  const existing = await getObraConfig()
  if (!existing && !patch.nome) return
  const ts = now()
  await db.obra_config.put({
    id: 1,
    ...(existing ?? {
      nome: patch.nome ?? '',
      dataInicio: patch.dataInicio ?? '',
      areaTotal: patch.areaTotal ?? 0,
      padraoConstrucao: patch.padraoConstrucao ?? '',
      regiao: patch.regiao ?? '',
      orcamentoTotal: patch.orcamentoTotal ?? 0,
      faseAtual: patch.faseAtual ?? '',
      tipoContratacao: patch.tipoContratacao ?? '',
    }),
    ...patch,
    criadoEm: existing?.criadoEm ?? patch.criadoEm ?? ts,
    atualizadoEm: ts,
  })
}

export async function hasOnboarded(): Promise<boolean> {
  const config = await getObraConfig()
  return config !== undefined
}

// ── Tópicos Progresso ────────────────────────────────────────

export async function getTopicoProgresso(topicoId: string): Promise<TopicoProgresso | undefined> {
  return db.topicos_progresso.where('topicoId').equals(topicoId).first()
}

export async function upsertTopicoProgresso(
  topicoId: string,
  moduloId: string,
  patch: Partial<Omit<TopicoProgresso, 'id' | 'topicoId' | 'moduloId' | 'atualizadoEm'>>,
): Promise<void> {
  const existing = await getTopicoProgresso(topicoId)
  const base: TopicoProgresso = existing ?? {
    topicoId,
    moduloId,
    concluido: false,
    percentualPratico: 0,
    anotacoes: '',
    checklistItems: [],
    atualizadoEm: now(),
  }
  await db.topicos_progresso.put({ ...base, ...patch, topicoId, moduloId, atualizadoEm: now() })
}

export async function getProgressoByModulo(moduloId: string): Promise<TopicoProgresso[]> {
  return db.topicos_progresso.where('moduloId').equals(moduloId).toArray()
}

export async function toggleFavorito(topicoId: string, moduloId: string): Promise<void> {
  const existing = await getTopicoProgresso(topicoId)
  await upsertTopicoProgresso(topicoId, moduloId, { favorito: !existing?.favorito })
}

export async function getFavoritos(): Promise<TopicoProgresso[]> {
  const all = await db.topicos_progresso.toArray()
  return all.filter((t) => t.favorito)
}

export async function countConcluidosByModulo(moduloId: string): Promise<number> {
  const all = await db.topicos_progresso.where('moduloId').equals(moduloId).toArray()
  return all.filter((t) => t.concluido).length
}

// ── Lançamentos Financeiros ──────────────────────────────────

export async function getLancamentos(): Promise<LancamentoFinanceiro[]> {
  return db.lancamentos_financeiros.orderBy('data').reverse().toArray()
}

export async function addLancamento(
  data: Omit<LancamentoFinanceiro, 'id' | 'criadoEm' | 'atualizadoEm'>,
): Promise<number> {
  const ts = now()
  return db.lancamentos_financeiros.add({ ...data, criadoEm: ts, atualizadoEm: ts })
}

export async function updateLancamento(
  id: number,
  patch: Partial<Omit<LancamentoFinanceiro, 'id' | 'criadoEm'>>,
): Promise<void> {
  await db.lancamentos_financeiros.update(id, { ...patch, atualizadoEm: now() })
}

export async function deleteLancamento(id: number): Promise<void> {
  const ts = now()
  await db.transaction(
    'rw',
    db.lancamentos_financeiros,
    db.orcamentos_materiais,
    db.contrato_parcelas,
    db.contratos,
    async () => {
      const parcelas = await db.contrato_parcelas.where('lancamentoId').equals(id).toArray()
      for (const p of parcelas) {
        if (p.id == null) continue
        await db.contrato_parcelas.update(p.id, {
          status: p.liberadaEm ? 'liberada' : 'prevista',
          dataPagamento: undefined,
          lancamentoId: undefined,
          atualizadoEm: ts,
        })
      }
      const contratoIds = [...new Set(parcelas.map((p) => p.contratoId))]
      for (const contratoId of contratoIds) {
        const c = await db.contratos.get(contratoId)
        if (c?.status === 'concluido') {
          await db.contratos.update(contratoId, { status: 'ativo', atualizadoEm: ts })
        }
      }
      const cotacoes = await db.orcamentos_materiais.where('lancamentoId').equals(id).toArray()
      for (const o of cotacoes) {
        if (o.id == null) continue
        await db.orcamentos_materiais.update(o.id, { lancamentoId: undefined, atualizadoEm: ts })
      }
      await db.lancamentos_financeiros.delete(id)
    },
  )
}

export async function getTotalGasto(): Promise<number> {
  const lancamentos = await db.lancamentos_financeiros.toArray()
  return lancamentos.filter((l) => l.status === 'pago').reduce((sum, l) => sum + l.valor, 0)
}

export async function getTotalPrevisto(): Promise<number> {
  const lancamentos = await db.lancamentos_financeiros.toArray()
  return lancamentos.reduce((sum, l) => sum + l.valor, 0)
}

export async function getTotalReceitas(): Promise<number> {
  const lancamentos = await db.lancamentos_financeiros.toArray()
  return lancamentos.filter((l) => l.tipo === 'receita').reduce((sum, l) => sum + l.valor, 0)
}

export async function saveOrcamentoCategorias(orcamento: Record<string, number>): Promise<void> {
  const existing = await getObraConfig()
  if (!existing) return
  await db.obra_config.update(1, { orcamentoPorCategoria: orcamento, atualizadoEm: now() })
}

// ── Documentos ───────────────────────────────────────────────

export async function getDocumentos(): Promise<Documento[]> {
  return db.documentos.orderBy('criadoEm').reverse().toArray()
}

export async function getDocumento(id: number): Promise<Documento | undefined> {
  return db.documentos.get(id)
}

export async function addDocumento(
  data: Omit<Documento, 'id' | 'criadoEm'>,
  arquivo?: Blob | File | null,
): Promise<number> {
  const id = await db.documentos.add({
    ...data,
    hasArquivo: Boolean(arquivo),
    criadoEm: now(),
  })

  if (arquivo) {
    const backend = await writeDocumentBlob(id, arquivo)
    await db.documentos.update(id, {
      arquivoBackend: backend,
      tamanho: data.tamanho ?? arquivo.size,
      hasArquivo: true,
    })
  }

  return id
}

export async function updateDocumento(
  id: number,
  patch: Partial<Omit<Documento, 'id' | 'criadoEm'>>,
  arquivo?: Blob | File | null,
): Promise<void> {
  const existing = await db.documentos.get(id)
  const nextPatch = { ...patch }

  if (arquivo) {
    const backend = await writeDocumentBlob(id, arquivo)
    Object.assign(nextPatch, {
      arquivoBackend: backend,
      tamanho: patch.tamanho ?? arquivo.size,
      hasArquivo: true,
    })
  }

  await db.documentos.update(id, nextPatch)

  if (patch.hasArquivo === false && !arquivo) {
    await deleteDocumentBlob(id, existing?.arquivoBackend)
  }
}

export async function deleteDocumento(id: number): Promise<void> {
  const existing = await db.documentos.get(id)
  await deleteDocumentBlob(id, existing?.arquivoBackend)
  await db.documentos.delete(id)
}

export async function getDocumentosStats(): Promise<Record<string, number>> {
  const docs = await db.documentos.toArray()
  const stats: Record<string, number> = {}
  for (const doc of docs) {
    stats[doc.categoria] = (stats[doc.categoria] ?? 0) + 1
  }
  return stats
}

// ── Alertas ──────────────────────────────────────────────────

export async function getAlertas(apenasAtivos = false): Promise<Alerta[]> {
  const all = await db.alertas.orderBy('criadoEm').reverse().toArray()
  if (!apenasAtivos) return all
  return all.filter((a) => (a.status ? a.status === 'ativo' : !a.lido))
}

export async function countAlertasAtivos(): Promise<number> {
  const all = await db.alertas.toArray()
  return all.filter((a) => (a.status ? a.status === 'ativo' : !a.lido)).length
}

export async function addAlerta(data: Omit<Alerta, 'id' | 'criadoEm'>): Promise<number> {
  return db.alertas.add({ ...data, criadoEm: now() })
}

export async function marcarAlertaLido(id: number): Promise<void> {
  await db.alertas.update(id, { lido: true, status: 'resolvido' })
}

export async function resolverAlerta(id: number): Promise<void> {
  await db.alertas.update(id, { lido: true, status: 'resolvido' })
}

export async function ignorarAlerta(id: number): Promise<void> {
  await db.alertas.update(id, { lido: true, status: 'ignorado' })
}

export async function getAlertaByChave(chave: string): Promise<Alerta | undefined> {
  return db.alertas.where('chave').equals(chave).first()
}

export async function resolverAlertasByChave(chaves: string[]): Promise<void> {
  if (chaves.length === 0) return
  const records = await db.alertas.where('chave').anyOf(chaves).toArray()
  const ids = records.filter((a) => a.status === 'ativo').map((a) => a.id!)
  for (const id of ids) {
    await db.alertas.update(id, { lido: true, status: 'resolvido' })
  }
}

// ── Diário ───────────────────────────────────────────────────

export async function getDiario(topicoId?: string): Promise<DiarioEntry[]> {
  const query = topicoId
    ? db.diario.where('topicoId').equals(topicoId)
    : db.diario.orderBy('criadoEm').reverse()
  return query.toArray()
}

export async function addDiarioEntry(
  data: Omit<DiarioEntry, 'id' | 'criadoEm' | 'atualizadoEm'>,
): Promise<number> {
  const ts = now()
  return db.diario.add({ ...data, criadoEm: ts, atualizadoEm: ts })
}

export async function updateDiarioEntry(
  id: number,
  patch: string | Partial<Omit<DiarioEntry, 'id' | 'criadoEm'>>,
): Promise<void> {
  const data = typeof patch === 'string' ? { texto: patch } : patch
  await db.diario.update(id, { ...data, atualizadoEm: now() })
}

export async function deleteDiarioEntry(id: number): Promise<void> {
  const entry = await db.diario.get(id)
  if (entry?.fotoIds?.length) {
    for (const fotoId of entry.fotoIds) {
      await deleteDocumento(fotoId).catch(() => undefined)
    }
  }
  await db.diario.delete(id)
}

// ── Configurações avançadas ──────────────────────────────────

export async function saveFaseConstrutiva(faseConstrutiva: string): Promise<void> {
  const existing = await getObraConfig()
  if (!existing) return
  await db.obra_config.update(1, { faseConstrutiva, atualizadoEm: now() })
}

export async function saveConfiguracoes(
  patch: Partial<
    Pick<
      ObraConfig,
      'valorMinimoNF' | 'dataAlvara' | 'reservaContingenciaPct' | 'dataPrevisaoTermino'
    >
  >,
): Promise<void> {
  const existing = await getObraConfig()
  if (!existing) return
  await db.obra_config.update(1, { ...patch, atualizadoEm: now() })
}

// ── Resetar progresso ────────────────────────────────────────

export async function resetarProgressoModulo(moduloId: string): Promise<void> {
  const ids = await db.topicos_progresso.where('moduloId').equals(moduloId).primaryKeys()
  await db.topicos_progresso.bulkDelete(ids as number[])
}

export async function resetarTodosProgresso(): Promise<void> {
  await db.topicos_progresso.clear()
}

const allTables = () => ALL_TABLES.map((name) => db.table(name))

export async function limparTodosDados(): Promise<void> {
  await db.transaction('rw', allTables(), async () => {
    await clearAllDocumentFiles()
    await Promise.all(allTables().map((t) => t.clear()))
  })
  clearAppLocalState()
  await clearServiceWorkerCaches()
}

// ── Orçamentos de Materiais ──────────────────────────────────

export async function listOrcamentosMateriais(): Promise<OrcamentoMaterial[]> {
  return db.orcamentos_materiais.orderBy('criadoEm').reverse().toArray()
}

export async function getOrcamentosByGrupo(grupoId: string): Promise<OrcamentoMaterial[]> {
  return db.orcamentos_materiais.where('grupoId').equals(grupoId).toArray()
}

export async function addOrcamentoMaterial(
  data: Omit<OrcamentoMaterial, 'id' | 'criadoEm' | 'atualizadoEm'>,
): Promise<number> {
  const ts = now()
  return db.orcamentos_materiais.add({ ...data, criadoEm: ts, atualizadoEm: ts })
}

export async function updateOrcamentoMaterial(
  id: number,
  patch: Partial<Omit<OrcamentoMaterial, 'id' | 'criadoEm'>>,
): Promise<void> {
  await db.orcamentos_materiais.update(id, { ...patch, atualizadoEm: now() })
}

export async function deleteOrcamentoMaterial(id: number): Promise<void> {
  await db.orcamentos_materiais.delete(id)
}

export async function escolherOrcamentoMaterial(
  grupoId: string,
  escolhidoId: number,
): Promise<void> {
  const grupo = await getOrcamentosByGrupo(grupoId)
  await db.transaction('rw', db.orcamentos_materiais, async () => {
    for (const o of grupo) {
      await db.orcamentos_materiais.update(o.id!, {
        status: o.id === escolhidoId ? 'escolhido' : 'cotado',
        atualizadoEm: now(),
      })
    }
  })
}

// ── Pendências da obra ───────────────────────────────────────

export async function getObraPendencias(apenasAbertas = false): Promise<ObraPendencia[]> {
  const all = await db.obra_pendencias.orderBy('criadoEm').reverse().toArray()
  if (!apenasAbertas) return all
  return all.filter((p) => p.status !== 'resolvida')
}

export async function countObraPendenciasAbertas(): Promise<number> {
  return db.obra_pendencias
    .filter((p) => p.tipo === 'pendencia' && p.status !== 'resolvida')
    .count()
}

export async function addObraPendencia(
  data: Omit<ObraPendencia, 'id' | 'criadoEm' | 'atualizadoEm'>,
): Promise<number> {
  const ts = now()
  return db.obra_pendencias.add({ ...data, criadoEm: ts, atualizadoEm: ts })
}

export async function updateObraPendencia(
  id: number,
  patch: Partial<Omit<ObraPendencia, 'id' | 'criadoEm'>>,
): Promise<void> {
  await db.obra_pendencias.update(id, { ...patch, atualizadoEm: now() })
}

export async function setObraPendenciaStatus(
  id: number,
  status: ObraPendenciaStatus,
): Promise<void> {
  const patch: Partial<ObraPendencia> = { status }
  if (status === 'resolvida') patch.resolvidoEm = now()
  await updateObraPendencia(id, patch)
}

export async function deleteObraPendencia(id: number): Promise<void> {
  await db.obra_pendencias.delete(id)
}

// ── Genérico ─────────────────────────────────────────────────

export async function clearTable<T, K>(table: Table<T, K>): Promise<void> {
  await table.clear()
}

/** Tabelas serializáveis no backup (blobs de arquivos ficam fora, tratados à parte). */
const DATA_TABLES = ALL_TABLES.filter((t) => t !== 'documento_arquivos')

export async function importDB(data: Record<string, unknown[]>): Promise<void> {
  await db.transaction('rw', allTables(), async () => {
    await clearAllDocumentFiles()
    await Promise.all(allTables().map((t) => t.clear()))
    for (const name of DATA_TABLES) {
      const rows = data[name]
      if (rows?.length) await db.table(name).bulkAdd(rows)
    }
  })
}

export async function exportDB(): Promise<Record<string, unknown[]>> {
  const entries = await Promise.all(
    DATA_TABLES.map(async (name) => [name, await db.table(name).toArray()] as const),
  )
  return Object.fromEntries(entries)
}
